import { EditorState, NodeSelection, TextSelection, Transaction } from 'prosemirror-state'
import { isInTable } from 'prosemirror-tables'

import { Command } from '../types'
import { CellSelection } from 'common/Prose/utils/table/CellSelection'
import { Axis, shiftArrow } from 'common/Prose/utils/table/input'
import { ResolvedPos } from 'prosemirror-model'
import { INLINE_DIRECTION } from '../constants'
import { schema } from 'prose-core'
import { Node as ProseNode } from 'prosemirror-model'
import { BorderSelection } from 'common/Prose/utils/table/BorderSelection'
import { LineMode, findTableFromPos } from 'common/Prose/utils/table'
import { intersection } from 'lodash'
import { TableMap } from 'common/Prose/utils/table/TableMap'
import { ContentNodeWithPos } from 'prosemirror-utils'
import { setDomSelection } from 'common/Prose/Plugins/tableSelection/tableSelectionUtils'
import { EditorView } from 'prosemirror-view'

export const _putSelectionAtBeginningOfTable = (state: EditorState, posBeforeTable: number): Transaction => {
  // 3: Enter Table -> Enter Table Cell -> Enter cellContent
  return state.tr.setSelection(TextSelection.create(state.doc, posBeforeTable + 3))
}

export const selectBorder = (
  lineMode: LineMode,
  posBeforeTable: number,
  rowOrColIdx: number,
  fragmentIdx?: number,
): Command => {
  return (state, dispatch) => {
    let horizontals: Array<{ rowOrColIdx: number; fragmentIdx?: number }> = []
    let verticals: Array<{ rowOrColIdx: number; fragmentIdx?: number }> = []

    let tableNode: ContentNodeWithPos
    let tableMap: TableMap

    if (fragmentIdx !== undefined) {
      // performance optimization
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      tableNode = findTableFromPos(posBeforeTable + 1, state.doc)!
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      tableMap = new TableMap({ posBeforeTable: tableNode!.pos, node: tableNode!.node })
    }

    /**
     * Given the coordinates of a border selection fragment
     * 1. find the two adjacent cells
     * 2. get the coords of those two cells
     * 3. get the axis indexes in common between the two sets of coords (considering only the dimension we're interested in, either X or Y)
     */
    const findIntersectingRowOrColIdx = (lineMode: LineMode, x: number, y: number) => {
      const adjacentCellCoords: Array<[number, number]> =
        lineMode === LineMode.Row
          ? [
              [y, Math.max(0, x - 1)],
              [y, Math.min(tableMap.numRows - 1, x)],
            ]
          : [
              [Math.max(0, y - 1), x],
              [Math.min(tableMap.numCols - 1, y), x],
            ]

      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const adjacentCellsPos = adjacentCellCoords.map((coord) => tableMap.getPosAtCoord(...coord)!)
      const adjacentCellsPosList = adjacentCellsPos.map((pos) => tableMap.getCoordListOfPos(pos))
      const [listOfAxisIndexesA, listOfAxisIndexesB] = adjacentCellsPosList.map((cellPosList) =>
        cellPosList.map((coord) => coord[lineMode === LineMode.Row ? 1 : 0]),
      )
      return intersection(listOfAxisIndexesA, listOfAxisIndexesB)
    }

    if (lineMode === LineMode.Row) {
      horizontals =
        fragmentIdx === undefined
          ? [{ rowOrColIdx }]
          : findIntersectingRowOrColIdx(lineMode, rowOrColIdx, fragmentIdx).map((colIdx) => ({
              rowOrColIdx,
              fragmentIdx: colIdx,
            }))
    } else {
      verticals =
        fragmentIdx === undefined
          ? [{ rowOrColIdx }]
          : findIntersectingRowOrColIdx(lineMode, fragmentIdx, rowOrColIdx).map((rowIdx) => ({
              rowOrColIdx,
              fragmentIdx: rowIdx,
            }))
    }
    const $tablePos = state.doc.resolve(posBeforeTable)
    const newSelection = new BorderSelection($tablePos, verticals, horizontals)
    dispatch?.(state.tr.setSelection(newSelection))
    return true
  }
}

export const selectColumn = (posBeforeTable: number, columnIndex: number): Command => {
  return (state, dispatch) => {
    const $posBeforeTable = state.doc.resolve(posBeforeTable)
    const table = $posBeforeTable.nodeAfter

    if (table) {
      const tr = state.tr.setSelection(CellSelection.columnSelection($posBeforeTable, columnIndex))
      dispatch?.(tr)
      return true
    }
    return false
  }
}

export const selectRow = (posBeforeTable: number, rowIndex: number): Command => {
  return (state, dispatch) => {
    const $posBeforeTable = state.doc.resolve(posBeforeTable)
    const table = $posBeforeTable.nodeAfter

    if (table) {
      const tr = state.tr.setSelection(CellSelection.rowSelection($posBeforeTable, rowIndex))
      dispatch?.(tr)
      return true
    }
    return false
  }
}

export const selectDownInTable: Command = (state, dispatch, view) => {
  if (!isInTable(state)) return false

  if (dispatch && view) {
    return shiftArrow(Axis.Vertical, 1)(state, dispatch, view)
  }

  return false
}

export const selectLeftInTable: Command = (state, dispatch, view) => {
  if (!isInTable(state)) return false

  if (dispatch && view) {
    return shiftArrow(Axis.Horizontal, -1)(state, dispatch, view)
  }

  return false
}

export const selectRightInTable: Command = (state, dispatch, view) => {
  if (!isInTable(state)) return false

  if (dispatch && view) {
    return shiftArrow(Axis.Horizontal, 1)(state, dispatch, view)
  }

  return false
}

export const selectUpInTable: Command = (state, dispatch, view) => {
  if (!isInTable(state)) return false

  if (dispatch && view) {
    return shiftArrow(Axis.Vertical, -1)(state, dispatch, view)
  }

  return false
}

export function toggleSelectTable(posBeforeTable: number, view: EditorView<any>): Command {
  return (state, dispatch) => {
    let { tr } = state
    const isTableSelected = state.selection instanceof NodeSelection && state.selection.from === posBeforeTable

    if (isTableSelected) {
      // Set Cursor in first Table Cell
      tr = _putSelectionAtBeginningOfTable(state, posBeforeTable)
      dispatch?.(tr)
    } else {
      // Select Table
      tr = state.tr.setSelection(NodeSelection.create(state.doc, posBeforeTable))
      dispatch?.(tr)
      setDomSelection(view.domAtPos(posBeforeTable + 1).node)
    }
    return true
  }
}

export const maybeSelectTable: (direction: INLINE_DIRECTION) => Command = (direction) => {
  return (state, dispatch) => {
    let { selection, tr } = state
    let $cursorPos: ResolvedPos | null
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (selection instanceof TextSelection && ($cursorPos = (selection as TextSelection).$cursor!)) {
      // this is a cursor selection
      let pos = $cursorPos.pos + direction
      let adjacentNode: ProseNode | null | undefined
      if (direction === INLINE_DIRECTION.FORWARDS) {
        adjacentNode = state.doc.resolve(pos).nodeAfter
      } else if (direction === INLINE_DIRECTION.BACKWARDS) {
        adjacentNode = state.doc.resolve(pos).nodeBefore
        if (adjacentNode?.type === schema.nodes.table) {
          // if we're looking backwards and we found a table, subtract the size from pos to get the
          // pos before the table
          pos -= adjacentNode.nodeSize
        }
      }
      if (adjacentNode?.type === schema.nodes.table) {
        tr.setSelection(NodeSelection.create(state.doc, pos))
        dispatch?.(tr)
        return true
      }
    }
    return false
  }
}
